import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { clearSession, getUser } from "../lib/auth";
import { ConfirmDialog } from "./ConfirmDialog";

/**
 * 侧栏导航：
 * - 上半为「列表类」管理菜单（项目库 / 智库提交·报告 / 动态 / BP / 招聘 / 联系），
 *   这些是增删改明细的数据列表。
 * - 底部「页面文案」是单一入口：进去后在官网预览里左键切页面、右键改文字，
 *   覆盖首页/关于/投资/智库/BP/联系及品牌·导航·页脚等所有页面固定文案。
 */
type NavItem = { label: string; to: string };
type NavBlock = { group?: string; items: NavItem[] };

const NAV_BLOCKS: NavBlock[] = [
  { items: [{ label: "页面文案", to: "/pages" }] },
  { items: [{ label: "投资项目", to: "/projects" }] },
  { items: [{ label: "用户管理", to: "/studio/users" }] },
  { items: [{ label: "智库提交", to: "/studio/submissions" }] },
  { items: [{ label: "智库报告", to: "/studio/reports" }] },
  { items: [{ label: "动态新闻", to: "/news" }] },
  { items: [{ label: "BP 提交", to: "/bp" }] },
  { items: [{ label: "招聘信息", to: "/jobs" }] },
  { items: [{ label: "联系咨询", to: "/contacts" }] },
];

// 仅超管可见的菜单（高危操作，如整库备份/恢复）
const ADMIN_ONLY_BLOCKS: NavBlock[] = [
  { items: [{ label: "网站备份", to: "/backup" }] },
  { items: [{ label: "网站维护", to: "/maintenance" }] },
];

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const user = getUser();
  const blocks = user?.role === "admin" ? [...NAV_BLOCKS, ...ADMIN_ONLY_BLOCKS] : NAV_BLOCKS;
  const nav = useNavigate();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const logout = () => {
    clearSession();
    nav("/login", { replace: true });
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <span className="sb-logo-box">
          珂
          <i className="c tl-h" /><i className="c tl-v" />
          <i className="c br-h" /><i className="c br-v" />
        </span>
        <span className="sb-logo-text">
          <span className="sb-logo-cn">玺 · 资本</span>
          <span className="sb-logo-en">KEXI CAPITAL</span>
        </span>
      </div>
      <nav className="sidebar-nav">
        {blocks.map((block, i) => (
          <div key={i} className={block.group ? "" : "sidebar-solo"}>
            {block.group && <div className="group-label">{block.group}</div>}
            {block.items.map((it) => (
              <NavLink key={it.to} to={it.to} onClick={onNavigate}
                className={({ isActive }) => (isActive ? "active" : undefined)}>
                {it.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="sidebar-footer">
        {/* 点击用户名进入个人信息页（改显示名/改密码）*/}
        <button
          className="sidebar-profile-btn"
          onClick={() => { nav("/profile"); onNavigate?.(); }}
          title="个人信息 / 修改密码">
          <div style={{ flex: 1, textAlign: "left", overflow: "hidden" }}>
            <div style={{ color: "var(--navy-ivory)", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
              {user?.display_name || user?.email}
            </div>
            <div style={{ marginTop: 2, fontSize: 12, opacity: 0.7 }}>{user?.role} · 个人信息</div>
          </div>
          <span aria-hidden style={{ fontSize: 18, lineHeight: 1, opacity: 0.6 }}>›</span>
        </button>
        <button className="btn" style={{ marginTop: 10, width: "100%", height: 28 }} onClick={() => setConfirmLogout(true)}>退出登录</button>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        title="退出登录"
        message="确认退出登录？退出后需重新登录才能进入后台。"
        confirmLabel="退出登录"
        onConfirm={() => { setConfirmLogout(false); logout(); }}
        onCancel={() => setConfirmLogout(false)}
      />
    </aside>
  );
}
